import { normalizeUserPreferences } from '../admin/userPreferencesAdapter.js';
import {
  DEFAULT_FOCUS_MODE_RESEARCH_REPORT_DISPLAY,
  normalizeFocusModeResearchReportDisplay,
} from './researchReports.js';

export const APP_THEME_LIGHT = 'light';
export const APP_THEME_DARK = 'dark';
export const APP_THEME_SYSTEM = 'system';
export const DEFAULT_APP_THEME = APP_THEME_LIGHT;
export const APP_THEME_STORAGE_KEY = 'topsail.appTheme';
export const APP_THEME_DARK_QUERY = '(prefers-color-scheme: dark)';

export const LOADING_INDICATOR_STYLE_SHIP = 'ship';
export const LOADING_INDICATOR_STYLE_CLASSIC = 'classic';
export const DEFAULT_LOADING_INDICATOR_STYLE = LOADING_INDICATOR_STYLE_SHIP;
export const LOADING_INDICATOR_STYLE_STORAGE_KEY =
  'topsail.loadingIndicatorStyle';

const APP_THEME_VALUES = new Set([
  APP_THEME_LIGHT,
  APP_THEME_DARK,
  APP_THEME_SYSTEM,
]);
const LOADING_INDICATOR_STYLE_VALUES = new Set([
  LOADING_INDICATOR_STYLE_SHIP,
  LOADING_INDICATOR_STYLE_CLASSIC,
]);

/**
 * Normalize a theme preference into a supported theme key.
 *
 * @param {unknown} value - Raw value from Firestore, storage, or the UI.
 * @returns {'light'|'dark'|'system'} Supported app theme.
 */
export function normalizeAppTheme(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return APP_THEME_VALUES.has(normalized) ? normalized : DEFAULT_APP_THEME;
}

/**
 * Resolve a supported theme preference into the concrete rendered mode.
 *
 * @param {unknown} value - Raw preference value to resolve.
 * @param {{ window?: Window|null }} [options={}] - Runtime adapters.
 * @returns {'light'|'dark'} Concrete rendered theme mode.
 */
export function resolveAppThemeMode(
  value,
  { window = globalThis?.window } = {}
) {
  const normalized = normalizeAppTheme(value);
  if (normalized === APP_THEME_DARK) {
    return APP_THEME_DARK;
  }
  if (normalized !== APP_THEME_SYSTEM) {
    return APP_THEME_LIGHT;
  }

  try {
    return window?.matchMedia?.(APP_THEME_DARK_QUERY)?.matches
      ? APP_THEME_DARK
      : APP_THEME_LIGHT;
  } catch {
    return APP_THEME_LIGHT;
  }
}

/**
 * Read the cached theme preference from browser storage.
 *
 * @param {{ getItem?: (key: string) => string|null }|null|undefined} [storage=globalThis.localStorage] - Storage adapter.
 * @returns {'light'|'dark'|'system'} Cached app theme.
 */
export function readStoredAppTheme(storage = globalThis?.localStorage) {
  try {
    return normalizeAppTheme(storage?.getItem?.(APP_THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_APP_THEME;
  }
}

/**
 * Cache a theme preference in browser storage.
 *
 * @param {unknown} value - Raw preference value to store.
 * @param {{ setItem?: (key: string, value: string) => void }|null|undefined} [storage=globalThis.localStorage] - Storage adapter.
 * @returns {'light'|'dark'|'system'} Stored app theme.
 */
export function rememberAppThemePreference(
  value,
  storage = globalThis?.localStorage
) {
  const normalized = normalizeAppTheme(value);
  try {
    storage?.setItem?.(APP_THEME_STORAGE_KEY, normalized);
  } catch {
    // Preference storage is best-effort; Firestore remains the source of truth.
  }
  return normalized;
}

/**
 * Apply a theme preference to the document root.
 *
 * @param {unknown} value - Raw preference value to apply.
 * @param {{ document?: Document|null, window?: Window|null }} [options={}] - Runtime adapters.
 * @returns {'light'|'dark'|'system'} Applied app theme.
 */
export function applyAppThemePreference(
  value,
  { document = globalThis?.document, window = globalThis?.window } = {}
) {
  const normalized = normalizeAppTheme(value);
  const resolved = resolveAppThemeMode(normalized, { window });
  const root = document?.documentElement;
  if (!root) {
    return normalized;
  }

  root.dataset.appTheme = normalized;
  root.dataset.resolvedAppTheme = resolved;
  root.style.colorScheme =
    normalized === APP_THEME_SYSTEM ? 'light dark' : resolved;
  return normalized;
}

/**
 * Apply the cached theme preference to the document root.
 *
 * @param {{ getItem?: (key: string) => string|null }|null|undefined} [storage=globalThis.localStorage] - Storage adapter.
 * @param {{ document?: Document|null }} [options={}] - Runtime adapters.
 * @returns {'light'|'dark'|'system'} Applied app theme.
 */
export function applyStoredAppThemePreference(
  storage = globalThis?.localStorage,
  options = {}
) {
  const theme = readStoredAppTheme(storage);
  return applyAppThemePreference(theme, options);
}

/**
 * Normalize a loading-indicator preference into a supported style key.
 *
 * @param {unknown} value - Raw value from Firestore, storage, or the UI.
 * @returns {'ship'|'classic'} Supported loading indicator style.
 */
export function normalizeLoadingIndicatorStyle(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return LOADING_INDICATOR_STYLE_VALUES.has(normalized)
    ? normalized
    : DEFAULT_LOADING_INDICATOR_STYLE;
}

/**
 * Read the cached loading-indicator preference from browser storage.
 *
 * @param {{ getItem?: (key: string) => string|null }|null|undefined} [storage=globalThis.localStorage] - Storage adapter.
 * @returns {'ship'|'classic'} Cached loading indicator style.
 */
export function readStoredLoadingIndicatorStyle(
  storage = globalThis?.localStorage
) {
  try {
    return normalizeLoadingIndicatorStyle(
      storage?.getItem?.(LOADING_INDICATOR_STYLE_STORAGE_KEY)
    );
  } catch {
    return DEFAULT_LOADING_INDICATOR_STYLE;
  }
}

/**
 * Cache a loading-indicator preference in browser storage.
 *
 * @param {unknown} value - Raw preference value to store.
 * @param {{ setItem?: (key: string, value: string) => void }|null|undefined} [storage=globalThis.localStorage] - Storage adapter.
 * @returns {'ship'|'classic'} Stored loading indicator style.
 */
export function rememberLoadingIndicatorStyle(
  value,
  storage = globalThis?.localStorage
) {
  const normalized = normalizeLoadingIndicatorStyle(value);
  try {
    storage?.setItem?.(LOADING_INDICATOR_STYLE_STORAGE_KEY, normalized);
  } catch {
    // Preference storage is best-effort; Firestore remains the source of truth.
  }
  return normalized;
}

/**
 * Load the saved loading-indicator style for a signed-in user.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: (path: string, ...args: unknown[]) => Promise<unknown> } }} services - Service container.
 * @returns {Promise<'ship'|'classic'>} Resolved loading indicator style.
 */
export async function loadLoadingIndicatorStyle(userId, services = {}) {
  if (!userId || !services?.fsService?.getDocument) {
    return DEFAULT_LOADING_INDICATOR_STYLE;
  }

  try {
    const prefs =
      (await services.fsService.getDocument(`UserPrefs/${userId}`, false)) ||
      {};
    return normalizeLoadingIndicatorStyle(prefs.loadingIndicatorStyle);
  } catch (err) {
    console.debug('[focusmode.js] loading indicator preference skipped', err);
  }
  return DEFAULT_LOADING_INDICATOR_STYLE;
}

/**
 * Load the saved app theme for a signed-in user.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: (path: string, ...args: unknown[]) => Promise<unknown> } }} services - Service container.
 * @returns {Promise<'light'|'dark'|'system'>} Resolved app theme.
 */
export async function loadAppTheme(userId, services = {}) {
  if (!userId || !services?.fsService?.getDocument) {
    return DEFAULT_APP_THEME;
  }

  try {
    const prefs =
      (await services.fsService.getDocument(`UserPrefs/${userId}`, false)) ||
      {};
    return normalizeAppTheme(prefs.appTheme);
  } catch (err) {
    console.debug('[focusmode.js] app theme preference skipped', err);
  }
  return DEFAULT_APP_THEME;
}

/**
 * Load the saved appearance preferences for a signed-in user.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: (path: string, ...args: unknown[]) => Promise<unknown> } }} services - Service container.
 * @returns {Promise<{ loadingIndicatorStyle: 'ship'|'classic', appTheme: 'light'|'dark'|'system' }>} Resolved appearance preferences.
 */
export async function loadAppearancePreferences(userId, services = {}) {
  const defaults = {
    loadingIndicatorStyle: DEFAULT_LOADING_INDICATOR_STYLE,
    appTheme: DEFAULT_APP_THEME,
  };
  if (!userId || !services?.fsService?.getDocument) {
    return defaults;
  }

  try {
    const prefs =
      (await services.fsService.getDocument(`UserPrefs/${userId}`, false)) ||
      {};
    return {
      loadingIndicatorStyle: normalizeLoadingIndicatorStyle(
        prefs.loadingIndicatorStyle
      ),
      appTheme: normalizeAppTheme(prefs.appTheme),
    };
  } catch (err) {
    console.debug('[focusmode.js] appearance preferences skipped', err);
  }
  return defaults;
}

/**
 * Load, cache, and apply a user's loading-indicator style.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: Function }, loading?: { setStyle?: (style: string) => string } }} services - Service container.
 * @param {{ storage?: { setItem?: (key: string, value: string) => void }|null }} [options={}] - Hydration options.
 * @returns {Promise<'ship'|'classic'>} Applied loading indicator style.
 */
export async function hydrateLoadingIndicatorStyle(
  userId,
  services = {},
  { storage = globalThis?.localStorage } = {}
) {
  const style = await loadLoadingIndicatorStyle(userId, services);
  rememberLoadingIndicatorStyle(style, storage);
  services?.loading?.setStyle?.(style);
  return style;
}

/**
 * Load, cache, and apply a user's app theme.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: Function } }} services - Service container.
 * @param {{ storage?: { setItem?: (key: string, value: string) => void }|null, document?: Document|null }} [options={}] - Hydration options.
 * @returns {Promise<'light'|'dark'|'system'>} Applied app theme.
 */
export async function hydrateAppTheme(
  userId,
  services = {},
  { storage = globalThis?.localStorage, document = globalThis?.document } = {}
) {
  const theme = await loadAppTheme(userId, services);
  rememberAppThemePreference(theme, storage);
  applyAppThemePreference(theme, { document });
  return theme;
}

/**
 * Load, cache, and apply a user's appearance preferences.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: Function }, loading?: { setStyle?: (style: string) => string } }} services - Service container.
 * @param {{ storage?: { setItem?: (key: string, value: string) => void }|null, document?: Document|null }} [options={}] - Hydration options.
 * @returns {Promise<{ loadingIndicatorStyle: 'ship'|'classic', appTheme: 'light'|'dark'|'system' }>} Applied appearance preferences.
 */
export async function hydrateAppearancePreferences(
  userId,
  services = {},
  { storage = globalThis?.localStorage, document = globalThis?.document } = {}
) {
  const prefs = await loadAppearancePreferences(userId, services);
  rememberLoadingIndicatorStyle(prefs.loadingIndicatorStyle, storage);
  rememberAppThemePreference(prefs.appTheme, storage);
  services?.loading?.setStyle?.(prefs.loadingIndicatorStyle);
  applyAppThemePreference(prefs.appTheme, { document });
  return prefs;
}

/**
 * Load Focus Mode user preferences for the signed-in user.
 *
 * Defaults to enabling tooltips and the mobile system dialer when the
 * preference document is missing.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: (path: string, ...args: unknown[]) => Promise<unknown> } }} services - Service container.
 * @returns {Promise<{
 *   focusModeTooltips: boolean,
 *   focusModeForceSystemDialerOnMobile: boolean,
 *   focusModeEmailBrainstormEnabled: boolean,
 *   focusModeEmailRewriteEnabled: boolean,
 *   focusModeCrmNotesOpenByDefault: boolean,
 *   focusModeResearchReportDisplay: 'accordion'|'single-modal'|'multi-modal',
 *   contactLocalCallWindowEnabled: boolean,
 *   contactLocalCallWindowMode: 'automatic'|'manual',
 *   contactLocalCallWindowStart: string,
 *   contactLocalCallWindowEnd: string,
 * }>} Resolved Focus Mode preference subset.
 */
export async function loadFocusModeUserPreferences(userId, services = {}) {
  const defaults = {
    ...normalizeUserPreferences(),
    focusModeResearchReportDisplay: DEFAULT_FOCUS_MODE_RESEARCH_REPORT_DISPLAY,
  };
  if (!userId) {
    return defaults;
  }
  const getter = services?.fsService?.getDocument;
  if (!getter) {
    return defaults;
  }
  try {
    const prefs = await getter.call(
      services.fsService,
      `UserPreferences/${userId}`,
      false
    );
    return {
      ...normalizeUserPreferences(prefs || {}),
      focusModeResearchReportDisplay: normalizeFocusModeResearchReportDisplay(
        prefs?.focusModeResearchReportDisplay
      ),
    };
  } catch (err) {
    console.debug('[focusmode.js] preferences load skipped', err);
  }
  return defaults;
}

/**
 * Load the focus mode tooltip preference for the signed-in user.
 *
 * Defaults to enabling tooltips when the preference document is missing.
 *
 * @param {string} userId - Authenticated user UID.
 * @param {{ fsService?: { getDocument?: (path: string, ...args: unknown[]) => Promise<unknown> } }} services - Service container.
 * @returns {Promise<boolean>} Resolves to whether tooltips should render.
 */
export async function loadFocusModeTooltipPreference(userId, services = {}) {
  const prefs = await loadFocusModeUserPreferences(userId, services);
  return prefs.focusModeTooltips;
}
